import {
  config,
  Errors,
  FormComponentProps,
  FormComponentRef,
  FormComponentResetSymbol,
  FormComponentSlotProps,
  FormDataConvertible,
  formDataToObject,
  isUrlMethodPair,
  mergeDataIntoQueryString,
  Method,
  resetFormFields,
  resolveUrlMethodPairComponent,
  UseFormUtils,
  VisitOptions,
} from '@inertiajs/core'
import { isEqual } from 'es-toolkit'
import { NamedInputEvent, ValidationConfig } from 'laravel-precognition'
import {
  computed,
  defineComponent,
  h,
  inject,
  InjectionKey,
  onBeforeUnmount,
  onMounted,
  PropType,
  provide,
  ref,
  SlotsType,
  watch,
} from 'vue'
import useForm from './useForm'

type FormSubmitOptions = Omit<VisitOptions, 'data' | 'onPrefetched' | 'onPrefetching'>
type FormSubmitter = HTMLElement | null

const noop = () => undefined

const FormContextKey: InjectionKey<FormComponentRef> = Symbol('InertiaFormContext')

const Form = defineComponent({
  name: 'Form',
  slots: Object as SlotsType<{
    default: FormComponentSlotProps
  }>,
  props: {
    action: {
      type: [String, Object] as PropType<FormComponentProps['action']>,
      default: '',
    },
    method: {
      type: String as PropType<FormComponentProps['method']>,
      default: 'get',
    },
    headers: {
      type: Object as PropType<FormComponentProps['headers']>,
      default: () => ({}),
    },
    queryStringArrayFormat: {
      type: String as PropType<FormComponentProps['queryStringArrayFormat']>,
      default: 'brackets',
    },
    errorBag: {
      type: [String, null] as PropType<FormComponentProps['errorBag']>,
      default: null,
    },
    showProgress: {
      type: Boolean,
      default: true,
    },
    transform: {
      type: Function as PropType<FormComponentProps['transform']>,
      default: (data: Record<string, FormDataConvertible>) => data,
    },
    options: {
      type: Object as PropType<FormComponentProps['options']>,
      default: () => ({}),
    },
    resetOnError: {
      type: [Boolean, Array] as PropType<FormComponentProps['resetOnError']>,
      default: false,
    },
    resetOnSuccess: {
      type: [Boolean, Array] as PropType<FormComponentProps['resetOnSuccess']>,
      default: false,
    },
    setDefaultsOnSuccess: {
      type: Boolean as PropType<FormComponentProps['setDefaultsOnSuccess']>,
      default: false,
    },
    onCancelToken: {
      type: Function as PropType<FormComponentProps['onCancelToken']>,
      default: noop,
    },
    onBefore: {
      type: Function as PropType<FormComponentProps['onBefore']>,
      default: noop,
    },
    onStart: {
      type: Function as PropType<FormComponentProps['onStart']>,
      default: noop,
    },
    onProgress: {
      type: Function as PropType<FormComponentProps['onProgress']>,
      default: noop,
    },
    onFinish: {
      type: Function as PropType<FormComponentProps['onFinish']>,
      default: noop,
    },
    onCancel: {
      type: Function as PropType<FormComponentProps['onCancel']>,
      default: noop,
    },
    onSuccess: {
      type: Function as PropType<FormComponentProps['onSuccess']>,
      default: noop,
    },
    onError: {
      type: Function as PropType<FormComponentProps['onError']>,
      default: noop,
    },
    onSubmitComplete: {
      type: Function as PropType<FormComponentProps['onSubmitComplete']>,
      default: noop,
    },
    disableWhileProcessing: {
      type: Boolean,
      default: false,
    },
    invalidateCacheTags: {
      type: [String, Array] as PropType<FormComponentProps['invalidateCacheTags']>,
      default: () => [],
    },
    validateFiles: {
      type: Boolean as PropType<FormComponentProps['validateFiles']>,
      default: false,
    },
    validationTimeout: {
      type: Number as PropType<FormComponentProps['validationTimeout']>,
      default: 1500,
    },
    optimistic: {
      type: Function as PropType<FormComponentProps['optimistic']>,
      default: undefined,
    },
    withAllErrors: {
      type: Boolean as PropType<FormComponentProps['withAllErrors']>,
      default: null,
    },
    component: {
      type: String as PropType<FormComponentProps['component']>,
      default: null,
    },
    instant: {
      type: Boolean as PropType<FormComponentProps['instant']>,
      default: false,
    },
  },
  setup(props, { slots, attrs, expose }) {
    const getTransformedData = (): Record<string, FormDataConvertible> => {
      const [_url, data] = getUrlAndData()

      return props.transform(data)
    }

    const form = useForm<Record<string, any>>({})
      .withPrecognition(
        () => method.value,
        () => getUrlAndData()[0],
      )
      .transform(getTransformedData)
      .setValidationTimeout(props.validationTimeout)

    if (props.validateFiles) {
      form.validateFiles()
    }

    if (props.withAllErrors ?? config.get('form.withAllErrors')) {
      form.withAllErrors()
    }

    const formElement = ref()
    const method = computed(() =>
      isUrlMethodPair(props.action) ? props.action.method : (props.method.toLowerCase() as Method),
    )
    const resolvedComponent = computed(() => {
      if (props.component) {
        return props.component
      }

      if (props.instant && isUrlMethodPair(props.action)) {
        return resolveUrlMethodPairComponent(props.action)
      }

      return null
    })

    // Can't use computed because FormData is not reactive
    const isDirty = ref(false)

    const defaultData = ref(new FormData())

    const onFormUpdate = (event: Event) => {
      if (event.type === 'reset' && (event as CustomEvent).detail?.[FormComponentResetSymbol]) {
        // When the form is reset programmatically, prevent native reset behavior
        event.preventDefault()
      }

      isDirty.value = event.type === 'reset' ? false : !isEqual(getData(), formDataToObject(defaultData.value))
    }

    const formEvents: Array<keyof HTMLElementEventMap> = ['input', 'change', 'reset']

    onMounted(() => {
      defaultData.value = getFormData()

      form.defaults(getData())

      formEvents.forEach((e) => formElement.value.addEventListener(e, onFormUpdate))
    })

    watch(
      () => props.validateFiles,
      (value) => (value ? form.validateFiles() : form.withoutFileValidation()),
    )

    watch(
      () => props.validationTimeout,
      (value) => form.setValidationTimeout(value),
    )

    onBeforeUnmount(() => formEvents.forEach((e) => formElement.value?.removeEventListener(e, onFormUpdate)))

    const getFormData = (submitter?: FormSubmitter): FormData => new FormData(formElement.value, submitter)

    // Convert the FormData to an object because we can't compare two FormData
    // instances directly (which is needed for isDirty), mergeDataIntoQueryString()
    // expects an object, and submitting a FormData instance directly causes problems with nested objects.
    const getData = (submitter?: FormSubmitter): Record<string, FormDataConvertible> =>
      formDataToObject(getFormData(submitter))

    const getUrlAndData = (submitter?: FormSubmitter): [string, Record<string, FormDataConvertible>] => {
      return mergeDataIntoQueryString(
        method.value,
        isUrlMethodPair(props.action) ? props.action.url : props.action,
        getData(submitter),
        props.queryStringArrayFormat,
      )
    }

    const submit = (submitter?: FormSubmitter) => {
      const [url, data] = getUrlAndData(submitter)
      const formTarget = (submitter as HTMLButtonElement | HTMLInputElement | null)?.getAttribute('formtarget')

      if (formTarget === '_blank' && method.value === 'get') {
        window.open(url, '_blank')
        return
      }

      const maybeReset = (resetOption: boolean | string[]) => {
        if (!resetOption) {
          return
        }

        if (resetOption === true) {
          reset()
        } else if (resetOption.length > 0) {
          reset(...resetOption)
        }
      }

      const submitOptions: FormSubmitOptions = {
        headers: props.headers,
        queryStringArrayFormat: props.queryStringArrayFormat,
        errorBag: props.errorBag,
        showProgress: props.showProgress,
        invalidateCacheTags: props.invalidateCacheTags,
        component: resolvedComponent.value,
        optimistic: props.optimistic ? (pageProps) => props.optimistic!(pageProps, data) : undefined,
        onCancelToken: props.onCancelToken,
        onBefore: props.onBefore,
        onStart: props.onStart,
        onProgress: props.onProgress,
        onFinish: props.onFinish,
        onCancel: props.onCancel,
        onSuccess: (...args) => {
          props.onSuccess?.(...args)
          props.onSubmitComplete?.(exposed)
          maybeReset(props.resetOnSuccess)

          if (props.setDefaultsOnSuccess === true) {
            defaults()
          }
        },
        onError: (...args) => {
          props.onError?.(...args)
          maybeReset(props.resetOnError)
        },
        ...props.options,
      }

      // We need transform because we can't override the default data with different keys (by design)
      form.transform(() => props.transform(data)).submit(method.value, url, submitOptions)

      // Reset the transformer back so the submitter is not used for future submissions
      form.transform(getTransformedData)
    }

    const reset = (...fields: string[]) => {
      resetFormFields(formElement.value, defaultData.value, fields)

      form.reset(...fields)
    }

    const clearErrors = (...fields: string[]) => {
      form.clearErrors(...fields)
    }

    const resetAndClearErrors = (...fields: string[]) => {
      clearErrors(...fields)
      reset(...fields)
    }

    const defaults = () => {
      defaultData.value = getFormData()
      isDirty.value = false
    }

    const exposed = {
      get errors() {
        return form.errors
      },
      get hasErrors() {
        return form.hasErrors
      },
      get processing() {
        return form.processing
      },
      get progress() {
        return form.progress
      },
      get wasSuccessful() {
        return form.wasSuccessful
      },
      get recentlySuccessful() {
        return form.recentlySuccessful
      },
      get validating() {
        return form.validating
      },
      clearErrors,
      resetAndClearErrors,
      setError: (fieldOrFields: string | Record<string, string>, maybeValue?: string) =>
        form.setError((typeof fieldOrFields === 'string' ? { [fieldOrFields]: maybeValue } : fieldOrFields) as Errors),
      get isDirty() {
        return isDirty.value
      },
      reset,
      submit,
      defaults,
      getData,
      getFormData,

      // Precognition
      touch: form.touch,
      valid: form.valid,
      invalid: form.invalid,
      touched: form.touched,
      validate: (field?: string | NamedInputEvent | ValidationConfig, config?: ValidationConfig) =>
        form.validate(...UseFormUtils.mergeHeadersForValidation(field, config, props.headers)),
      validator: () => form.validator(),
    }

    expose<FormComponentRef>(exposed)

    provide(FormContextKey, exposed)

    return () => {
      return h(
        'form',
        {
          ...attrs,
          ref: formElement,
          action: isUrlMethodPair(props.action) ? props.action.url : props.action,
          method: method.value,
          onSubmit: (event) => {
            event.preventDefault()
            submit(event.submitter)
          },
          inert: props.disableWhileProcessing && form.processing,
        },
        slots.default ? slots.default(exposed) : [],
      )
    }
  },
})

export function useFormContext<TForm extends object = Record<string, any>>(): FormComponentRef<TForm> | undefined {
  return inject(FormContextKey) as FormComponentRef<TForm> | undefined
}

type TypedFormComponent<TForm extends Record<string, any>> = Omit<typeof Form, 'new'> & {
  new (...args: ConstructorParameters<typeof Form>): Omit<InstanceType<typeof Form>, '$props' | '$slots'> & {
    $props: Omit<
      InstanceType<typeof Form>['$props'],
      'optimistic' | 'transform' | 'resetOnSuccess' | 'resetOnError' | 'onSubmitComplete'
    > & {
      optimistic?: FormComponentProps<TForm>['optimistic']
      transform?: FormComponentProps<TForm>['transform']
      resetOnSuccess?: FormComponentProps<TForm>['resetOnSuccess']
      resetOnError?: FormComponentProps<TForm>['resetOnError']
      onSubmitComplete?: FormComponentProps<TForm>['onSubmitComplete']
    }
    $slots: {
      default: (props: FormComponentSlotProps<TForm>) => any
    }
  }
}

export function createForm<TForm extends Record<string, any>>(): TypedFormComponent<TForm> {
  return Form as TypedFormComponent<TForm>
}

export default Form
